/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { animate, style, transition, trigger } from "@angular/animations";
import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	type ModelSignal,
	Output,
	TemplateRef,
	ViewEncapsulation,
	contentChild,
	effect,
	forwardRef,
	inject,
	input,
	linkedSignal,
	model,
	viewChild,
} from "@angular/core";
import { FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import * as moment from "moment";
import "moment/min/locales";
import { CbCalendar } from "../../../@classes/abstract/calendar";
import { PopoverDirective } from "../floating/popover/popover.directive";
import { PopoverModule } from "../floating/popover/popover.module";
import { TooltipModule } from "../floating/tooltip/tooltip.module";
import { IconComponent } from "../icon/icon.component";

const noop = () => {};

const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => DatepickerComponent),
	multi: true,
};

export type DatepickerDisabledDates = {
	dates?: Date[];
	weekdays?: number[];
	months?: number[];
	years?: number[];
	periods?: { start: Date; end: Date }[];
};

export enum DatepickerDotColor {
	moss = "moss",
	light_green = "light-green",
	green = "green",
	teal = "teal",
	cyan = "cyan",
	light_blue = "light-blue",
	blue = "blue",
	dark_blue = "dark-blue",
	indigo = "indigo",
	violet = "violet",
	purple = "purple",
	fuchsia = "fuchsia",
	pink = "pink",
	rose = "rose",
	dark_orange = "dark-orange",
	orange = "orange",
	yellow = "yellow",
	red = "red",
	gray = "gray",
	black = "black",
}

export interface CalendarCell {
	id: string;
	isCurrentMonth: boolean;
	day: CalendarDay;
	isDisabled?: boolean;
}

export interface CalendarDay {
	date: Date;
	hasNotifications?: boolean;
	events: CalendarEvent[];
	isToday: boolean;

	isSelected: boolean;
	isDisabled?: boolean;
}

export interface CalendarEvent {
	startDate: Date;
	endDate: Date;
	color?: DatepickerDotColor;
	data: unknown;
}

@Component({
	selector: "cb-datepicker",

	imports: [
		CommonModule,
		TranslateModule,
		FormsModule,
		IconComponent,
		TooltipModule,
		PopoverModule,
		TooltipModule,
	],
	templateUrl: "./datepicker.component.html",
	styleUrl: "./datepicker.component.scss",
	providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
	animations: [
		trigger("ContentAnimation", [
			transition(":enter", [
				style({ opacity: 0, transform: "scale(1.1)" }),
				animate(
					"100ms ease-out",
					style({ opacity: "1", transform: "scale(1)" }),
				),
			]),
			transition(":leave", [
				style({ opacity: 1, transform: "scale(1)" }),
				animate(
					"100ms ease-out",
					style({ opacity: "0", transform: "scale(0.9)" }),
				),
			]),
		]),
	],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatepickerComponent extends CbCalendar {
	public override currentDate: ModelSignal<Date> = model<Date>(new Date());

	private readonly translateService: TranslateService =
		inject(TranslateService);
	private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

	/**
	 * Placeholder of the input
	 */
	public readonly placeholder = input<string>();

	/**
	 * Label of the input
	 */
	public readonly label = input<string>();

	/**
	 * Hint of the input
	 */
	public readonly hint = input<string>();

	/**
	 * Info of the input displayed with a question mark
	 * when this is defined as trailing icon
	 */
	public readonly info = input<string>();

	/**
	 * If the input is required
	 */
	public readonly required = input<boolean>();

	/**
	 * If the input is disabled
	 */
	public readonly disabled = input(false);

	/**
	 * Leading icon of the input
	 */
	public readonly icon = input("cb-base-outline-calendar");

	/**
	 * Whether to show the period shortcuts
	 */
	public readonly shortcuts = input<boolean | undefined>(false);

	/**
	 * Whether to show the reset button
	 */
	public readonly resetButton = input<boolean | undefined>(true);

	/**
	 * Whether the input has an error
	 */
	public readonly error = input<boolean>();

	/**
	 * HTML autocomplete standard attribute
	 */
	public readonly autocomplete = input<string>();

	/**
	 * HTML name standard attribute
	 */
	public readonly name = input<string>();

	/**
	 * The disabled dates of the datepicker
	 */
	public readonly inputDisabledDates = input<DatepickerDisabledDates>(
		{},
		{ alias: "disabledDates" },
	);

	/**
	 * The events to show as dots
	 */
	public readonly inputEvents = input<CalendarEvent[]>([], {
		alias: "events",
	});

	/**
	 * The minimum date of the datepicker
	 */
	public readonly inputMinDate = input<Date | undefined>(undefined, {
		alias: "minDate",
	});

	/**
	 * If the minimum date is today
	 */
	public readonly minDateToday = input(false);

	/**
	 * The maximum date of the datepicker
	 */
	public readonly inputMaxDate = input<Date | undefined>(undefined, {
		alias: "maxDate",
	});

	/**
	 * The date format of the datepicker
	 */
	public readonly dateFormat = input("DD/MM/yyyy");

	/**
	 * The selection mode of the datepicker
	 */
	public readonly selectionMode = input<"single" | "range" | "multiple">(
		"single",
	);

	/**
	 * If the datepicker is in touch mode
	 */
	public readonly touchMode = input(false);

	/**
	 * Datepicker selection type
	 */
	public readonly selectionType = input<"day" | "month" | "year">("day");

	/**
	 * Emits the value of the input on change
	 */
	// public readonly change = output<null | string | string[]>();

	@Output() public change: EventEmitter<null | string | string[]> =
		new EventEmitter<null | string | string[]>();

	/**
	 * The header template
	 */
	readonly headerTemplate = contentChild("header", { read: TemplateRef });

	/**
	 * The footer template
	 */
	readonly footerTemplate = contentChild("footer", { read: TemplateRef });

	/**
	 * Reference to the popover element
	 */
	readonly popover = viewChild(PopoverDirective);

	public currentSelectionType = linkedSignal(() => this.selectionType());

	// public superSelectionType = computed(() => {
	// 	const mode = this.selectionType();

	// 	switch (mode) {
	// 		case "day":
	// 			return "month";
	// 		case "month":
	// 			return "year";
	// 		case "year":
	// 			return "multi-year";
	// 	}
	// });

	constructor() {
		super();

		effect(() => {
			this.events.set(this.inputEvents());
		});

		effect(() => {
			this.minDate.set(this.inputMinDate());
		});

		effect(() => {
			this.maxDate.set(this.inputMaxDate());
		});

		effect(() => {
			this.disabledDates.set(this.inputDisabledDates());
		});

		// // RIMUOVERE QUESTO EFFECT:
		// effect(() => {
		// 	this.selectedDates.set([this.currentDate()]);
		// });

		// // Potrebbe essere utile un effect per sincronizzare la VISTA con la selezione,
		// // ma non il contrario. Esempio:
		// effect(
		// 	() => {
		// 		const dates = this.selectedDates();

		// 		// Se c'è una selezione e la vista corrente non mostra già il mese/anno della selezione,
		// 		// aggiorna la vista (currentDate o metodi specifici come setMonth/setYear).
		// 		this.updateDates(dates);
		// 	},
		// 	{ allowSignalWrites: true },
		// ); // Necessario se modifichi segnali (come currentDate) dentro l'effect

		effect(() => {
			const mode = this.currentSelectionType();

			switch (mode) {
				case "day":
					this.viewMode.set("month");
					break;
				case "month":
					this.viewMode.set("year");
					break;
				case "year":
					this.viewMode.set("multi-year");
					break;
			}
		});
	}

	ngOnInit(): void {
		this.calculate();
		this.resetPopover();
	}

	private get language(): string {
		return this.translateService.currentLang ?? "it";
	}

	public get strValue(): string {
		if (this.selectedDates().length > 0) {
			const firstDate = this.selectedDates()[0];
			const lang = this.language;
			const format = this.dateFormat();

			switch (this.selectionMode()) {
				case "single":
					return moment.default(firstDate).locale(lang).format(format);
				// ... (casi range e multiple potrebbero richiedere aggiustamenti simili)
				case "range": {
					// ... (la logica per range/multiple con formato solo mese/anno va definita)
					// Per semplicità, manteniamo il formato originale per range/multiple
					// o gestiamo la formattazione in modo specifico.
					// Qui usiamo il formato originale per non complicare:
					const originalFormat = this.dateFormat();
					if (this.selectedDates().length > 1) {
						return `${moment.default(this.selectedDates()[0]).locale(lang).format(originalFormat)} - ${moment.default(this.selectedDates()[1]).locale(lang).format(originalFormat)}`;
					}
					return moment.default(firstDate).locale(lang).format(originalFormat);
				}

				case "multiple": {
					const originalFormatMulti = this.dateFormat();
					return this.selectedDates()
						.map((d) =>
							moment.default(d).locale(lang).format(originalFormatMulti),
						)
						.join(", ");
				}
			}
		}
		return "";
	}

	/**
	 * Returns the value of the input
	 */
	public get value(): Date[] {
		return this.selectedDates();
	}

	/**
	 * Sets the value of the input
	 */
	public set value(v: Date[]) {
		if (v !== this.selectedDates()) {
			this.selectedDates.set(v);

			if (this.selectionMode() === "single") {
				let dateStr = null;
				if (this.selectedDates().length > 0) {
					dateStr = this.toDateString(this.selectedDates()[0]);
				}

				this.onChangeCallback(dateStr);
				this.change.emit(dateStr);
			} else {
				const dateStr = this.selectedDates().map((d) => this.toDateString(d));
				this.onChangeCallback(dateStr);
				this.change.emit(dateStr);
			}
		}
	}

	public get canReset(): boolean {
		return this.resetButton() === true && this.value.length > 0;
	}

	public get yearsRangeOptions(): number[] {
		const selectedYear = this.currentYear();

		// Get the range of year in batches of 10 where the selected year is included
		const start = selectedYear - (selectedYear % 10);
		const end = start + 9;

		const range = [];
		for (let i = start; i <= end; i++) {
			range.push(i);
		}

		return range;
	}

	public toDateString(date: Date): string {
		const d = date.getDate();
		const m = date.getMonth() + 1;
		const y = date.getFullYear();

		return `${y}-${m}-${d}`;
	}

	public writeValue(obj: Date[] | string | string[] | undefined): void {
		let newDates: Date[] = [];
		// ... (logica per convertire obj in newDates) ...
		if (obj !== undefined && obj !== null && obj !== "") {
			if (Array.isArray(obj)) {
				newDates = obj.map((d) => new Date(d));
			} else {
				newDates = [new Date(obj)];
			}
		}

		// Confronta gli array per evitare aggiornamenti non necessari
		// (Questa è una comparazione semplice, potresti aver bisogno di una più robusta)
		const currentValJson = JSON.stringify(
			this.selectedDates().map((d) => d.toISOString()),
		);
		const newValJson = JSON.stringify(newDates.map((d) => d.toISOString()));

		if (currentValJson !== newValJson) {
			this.selectedDates.set([]);
			this.selectedDates.set(structuredClone(newDates));
			this.updateDates(newDates);

			// L'effect sopra dovrebbe gestire l'aggiornamento della vista (currentDate)
			// Ma ricalcolare qui è sicuro
			this.calculate();
			// Chiamare detectChanges per aggiornare l'input associato a strValue
			this.cdr.detectChanges();
		} else if (newDates.length === 0 && this.selectedDates().length > 0) {
			// Handle case where value is explicitly set to null/empty
			this.selectedDates.set([]);
			this.calculate();
			this.cdr.detectChanges();
		}
	}

	public updateDates(dates: Date[]) {
		if (dates.length > 0) {
			const firstSelected = dates[0];
			if (
				this.currentYear() !== firstSelected.getFullYear() ||
				this.currentMonth() !== firstSelected.getMonth()
			) {
				this.currentDate.set(new Date(firstSelected)); // Imposta la data per la vista
				this.calculate(); // Ricalcola il calendario per il nuovo mese/anno
			}
		}
	}

	public onTouchedCallback: () => void = noop;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public onChangeCallback: (_: any) => void = noop;

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnChange(fn: any) {
		this.onChangeCallback = fn;
	}

	// From ControlValueAccessor interface
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	public registerOnTouched(fn: any) {
		this.onTouchedCallback = fn;
	}

	public previousSelectionType(): void {
		switch (this.currentSelectionType()) {
			case "day":
				this.currentSelectionType.set("month");
				break;
			case "month":
				this.currentSelectionType.set("year");
				break;
		}
		this.cdr.detectChanges();
	}

	public nextSelectionType(): void {
		switch (this.currentSelectionType()) {
			case "year":
				if (this.selectionType() !== "year") {
					this.currentSelectionType.set("month");
				}
				break;
			case "month":
				if (this.selectionType() === "day") {
					this.currentSelectionType.set("day");
				}
				break;
		}
		this.cdr.detectChanges();
	}

	public override previous(): void {
		super.previous();
		this.cdr.detectChanges();
	}

	public override next(): void {
		super.next();
		this.cdr.detectChanges();
	}

	public daySelected(day: CalendarDay): void {
		console.log("day", day);
		if (day.isDisabled) return;

		if (this.selectionType() === "day") {
			switch (this.selectionMode()) {
				case "single":
					this.value = [day.date];
					this.closePopover();
					break;
				case "range":
					console.log("day.date", day.date);
					if (this.value.length === 0) {
						this.value = [day.date];
					} else if (this.value.length === 1) {
						// Check that the selected date is after the first date
						if (day.date < this.value[0]) {
							this.value = [day.date];
						} else {
							// Check for every day between the two dates
							// that there are no disabled dates
							const startDate = this.value[0];
							const endDate = day.date;
							let disabled = false;
							for (
								let d = new Date(startDate);
								d <= endDate;
								d.setDate(d.getDate() + 1)
							) {
								if (this.isDayDisabled(d)) {
									disabled = true;
									break;
								}
							}

							if (!disabled) {
								const v = [startDate, endDate];
								this.value = v;
							} else {
								this.value = [day.date];
							}
						}
					} else {
						this.value = [day.date];
					}
					break;
				case "multiple": {
					const index = this.value.findIndex((d) =>
						this.isSameDay(d, day.date),
					);
					if (index === -1) {
						this.value.push(day.date);
					} else {
						this.value.splice(index, 1);
					}
					break;
				}
			}
			this.calculate();
			this.cdr.detectChanges();
		}
	}

	public monthSelected(day: CalendarDay): void {
		const date = day.date;
		const month = day.date.getMonth();
		this.currentDate.set(date);
		if (this.selectionType() === "month") {
			switch (this.selectionMode()) {
				case "single":
					this.value = [new Date(date.getFullYear(), month, 1)];
					this.closePopover();
					break;
				case "range":
					if (this.value.length === 0) {
						this.value = [new Date(date.getFullYear(), month, 1)];
					} else if (this.value.length === 1) {
						if (date < this.value[0]) {
							this.value = [date];
						} else {
							const startDate = this.value[0];
							const endDate = day.date;
							let disabled = false;
							for (
								let d = new Date(startDate);
								d <= endDate;
								d.setDate(d.getDate() + 1)
							) {
								if (this.isDayDisabled(d)) {
									disabled = true;
									break;
								}
							}
							if (!disabled) {
								const v = [startDate, endDate];
								this.value = v;
							} else {
								this.value = [day.date];
							}
						}
					} else {
						this.value = [day.date];
					}
					break;
			}
		} else {
			this.currentSelectionType.set("day");
			this.setMonth(month);
		}
		this.cdr.detectChanges();
	}

	public yearSelected(year: number): void {
		if (this.selectionType() === "year") {
			this.value = [new Date(year, 0, 1)];
		} else {
			this.currentSelectionType.set("month");
			this.setYear(year);
		}
		this.cdr.detectChanges();
	}

	protected override isDateSelected(d: Date): boolean {
		if (this.selectedDates().length === 0) return false;

		switch (this.selectionMode()) {
			case "single":
				return this.isSameDay(this.selectedDates()[0], d);
			case "range":
				if (this.selectedDates().length === 2) {
					return d >= this.selectedDates()[0] && d <= this.selectedDates()[1];
				}
				return this.isSameDay(this.selectedDates()[0], d);
			case "multiple":
				return (
					this.selectedDates().findIndex((date) => this.isSameDay(date, d)) !==
					-1
				);
		}
	}

	public periodShortcutSelected(
		shortcut:
			| "this_week"
			| "last_7_days"
			| "this_month"
			| "last_30_days"
			| "last_3_months"
			| "last_6_months"
			| "this_year"
			| "last_12_months",
	): void {
		const now = new Date();
		const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

		switch (shortcut) {
			case "this_week": {
				const day = today.getDay();
				const diff = day === 0 ? -6 : 1 - day;
				const monday = new Date(
					today.getFullYear(),
					today.getMonth(),
					today.getDate() + diff,
				);
				const sunday = new Date(
					monday.getFullYear(),
					monday.getMonth(),
					monday.getDate() + 6,
				);

				this.value = [monday, sunday];
				break;
			}
			case "last_7_days":
				this.value = [
					new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6),
					today,
				];
				break;
			case "this_month": {
				const firstDayOfMonth = new Date(
					today.getFullYear(),
					today.getMonth(),
					1,
				);
				const lastDayOfMonth = new Date(
					today.getFullYear(),
					today.getMonth() + 1,
					0,
				); // Adjusted to get the last day of the month
				this.value = [firstDayOfMonth, lastDayOfMonth];
				break;
			}
			case "last_30_days":
				this.value = [
					new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29),
					today,
				];
				break;
			case "last_3_months":
				this.value = [
					new Date(today.getFullYear(), today.getMonth() - 2, today.getDate()),
					today,
				];
				break;
			case "last_6_months":
				this.value = [
					new Date(today.getFullYear(), today.getMonth() - 5, today.getDate()),
					today,
				];
				break;
			case "this_year": {
				const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
				const lastDayOfYear = new Date(today.getFullYear(), 11, 31); // Adjusted to get December 31st
				this.value = [firstDayOfYear, lastDayOfYear];
				break;
			}
			case "last_12_months":
				this.value = [
					new Date(today.getFullYear() - 1, today.getMonth(), today.getDate()),
					today,
				];
				break;
		}

		this.calculate();
		this.cdr.detectChanges();
	}

	public reset(): void {
		this.value = [];
		this.calculate();
		this.cdr.detectChanges();
	}

	public closePopover(): void {
		this.popover()?.close();
	}

	public resetPopover(): void {
		this.currentSelectionType.set(this.selectionType());
	}
}
